import { probeSse, type SseRefusal } from './sse'

/*
 * One SSE connection per browser for each event stream (SSE-TABS, 20.10.2026).
 *
 * The center allows a few streams per user (decision 51); every signed-in tab
 * used to open its own, so a phone, a PC and a handful of tabs ran into the
 * limit and the extra tabs showed "Reconnecting…" forever. Now the tabs of one
 * origin share one connection per stream:
 *
 * - Leader: the tab that holds the Web Lock `strevix-live-<stream>` keeps the
 *   EventSource and posts every event to the others over a BroadcastChannel of
 *   the same name. app.… and control.… are different origins: one connection each.
 * - Followers apply the leader's events. A new follower says hello; the leader
 *   answers with its connection state and, when the subscriber gives one, the
 *   current state as a snapshot event, so the tab does not wait for the next
 *   event. Events before that answer are skipped (the snapshot already has them).
 * - The leader closes its tab → the lock is free → the next tab takes it and
 *   opens the stream; the center's snapshot on connect reaches every tab.
 * - A frozen leader (a background tab the browser stopped) answers no ping:
 *   a visible follower steals the lock (steal: true) and leads.
 * - Every tab of the origin hidden for HIDDEN_CLOSE_MS (HIDDEN_CLOSE_PHONE_MS
 *   on a phone) → the leader closes the stream; the first tab to become
 *   visible again opens it (fresh snapshot).
 * - No navigator.locks or BroadcastChannel → each tab leads alone (as before).
 *
 * The stream itself: the browser's own reconnect after a drop (state
 * "reconnecting"); a refused stream (closed) is asked once with fetch (sse.ts):
 * 429 → "limited" until Retry-After, else a new try after 2 s growing to 30 s.
 */

/** Every tab of the origin hidden this long → the stream is closed until one is visible. */
export const HIDDEN_CLOSE_MS = 5 * 60_000
/**
 * The same on a phone (owner, 22.10): a phone in a pocket or on the home screen holds a
 * stream of the user's limit for nothing, and its browser freezes hidden tabs anyway.
 * Back on the screen the stream opens again with a fresh snapshot (~1 s).
 */
export const HIDDEN_CLOSE_PHONE_MS = 60_000

/**
 * A phone (or a tablet): a touch screen with no hover. Not the window width — a narrow
 * browser window on a computer is not a phone.
 */
export function isPhone(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia('(hover: none) and (pointer: coarse)').matches
  } catch {
    return false
  }
}

/** How long every tab may stay hidden before the stream closes, on this device. */
export function hiddenCloseMs(): number {
  return isPhone() ? HIDDEN_CLOSE_PHONE_MS : HIDDEN_CLOSE_MS
}
/** A visible follower checks the leader this often… */
export const PING_MS = 10_000
/** …and takes over when it does not answer within this. */
export const PONG_WAIT_MS = 3_000
/** A follower not heard from for this long no longer counts as visible. */
const VISIBLE_STALE_MS = 3 * PING_MS

export type SharedSseState = 'connecting' | 'open' | 'reconnecting' | 'limited' | 'paused'

export interface SharedSseInfo {
  /** limited: when (browser ms) the stream is tried again */
  retryAt?: number
  /** limited: the center's limit and the user's open streams, when the 429 says */
  limit?: number
  open?: number
  /** reconnecting: the server refused the stream (an answer other than 429), not a drop */
  refused?: boolean
}

export interface SharedSseEvent {
  name: string
  data: string
  lastEventId?: string
}

export interface SharedSseOptions {
  /** the stream's URL, e.g. /api/events */
  path: string
  /** the lock and channel name: strevix-live-<key> */
  key: string
  /** the event names to listen to */
  events: readonly string[]
  onEvent: (e: SharedSseEvent) => void
  onState: (s: SharedSseState, info: SharedSseInfo) => void
  /**
   * The leader's current state as one event for a tab that just joined (the
   * subscriber's reducer applies it like the center's snapshot); null = nothing yet.
   */
  snapshot?: () => SharedSseEvent | null
  /**
   * The name of the stream's own snapshot event (default "snapshot"): a new leader's first
   * event of that name makes a follower that waits for the state whole again.
   */
  snapshotEvent?: string
}

type Msg =
  | { t: 'hello'; from: string; visible: boolean }
  | { t: 'welcome'; to: string; state: SharedSseState; info: SharedSseInfo; snapshot: SharedSseEvent | null }
  | { t: 'event'; e: SharedSseEvent }
  | { t: 'state'; state: SharedSseState; info: SharedSseInfo }
  | { t: 'leader'; from: string }
  | { t: 'vis'; from: string; visible: boolean }
  | { t: 'bye'; from: string }
  | { t: 'ping'; from: string; visible: boolean }
  | { t: 'pong'; to: string }

/** Test hook: the share is off (each tab its own stream) when the browser lacks either API. */
export function sharingSupported(): boolean {
  return typeof BroadcastChannel !== 'undefined' && typeof navigator !== 'undefined' && !!navigator.locks && typeof navigator.locks.request === 'function'
}

function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden'
}

let seq = 0
function newId(): string {
  const r = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`
  return `${r}-${++seq}`
}

/** Opens (or joins) the shared stream; returns the function that leaves it. */
export function subscribeSharedSse(o: SharedSseOptions): () => void {
  const s = new SharedSse(o)
  s.start()
  return () => s.stop()
}

class SharedSse {
  private readonly id = newId()
  private readonly share = sharingSupported()
  private ch: BroadcastChannel | null = null
  private stopped = false
  private leader = false
  /** follower: the leader's welcome or a fresh snapshot arrived; events before it are skipped */
  private ready = false
  private state: SharedSseState = 'connecting'
  private info: SharedSseInfo = {}
  /** leader: resolves to release the lock */
  private releaseLock: (() => void) | null = null
  private lockAbort: AbortController | null = null
  // leader: the stream
  private es: EventSource | null = null
  private retryTimer: ReturnType<typeof setTimeout> | undefined
  private delay = 2000
  private paused = false
  private hiddenTimer: ReturnType<typeof setTimeout> | undefined
  /** leader: followers that said they are visible, and when they last said so */
  private visibleTabs = new Map<string, number>()
  // follower: the leader check
  private pingTimer: ReturnType<typeof setInterval> | undefined
  private pongTimer: ReturnType<typeof setTimeout> | undefined

  constructor(private readonly o: SharedSseOptions) {}

  start() {
    document.addEventListener('visibilitychange', this.onVisibility)
    window.addEventListener('pagehide', this.onPageHide)
    if (!this.share) {
      this.becomeLeader()
      return
    }
    this.mark()
    this.ch = new BroadcastChannel(this.o.key)
    this.ch.onmessage = (ev: MessageEvent<Msg>) => this.onMessage(ev.data)
    this.requestLock(false)
    this.post({ t: 'hello', from: this.id, visible: isVisible() })
    this.armPing()
  }

  stop() {
    if (this.stopped) return
    this.stopped = true
    document.removeEventListener('visibilitychange', this.onVisibility)
    window.removeEventListener('pagehide', this.onPageHide)
    this.post({ t: 'bye', from: this.id })
    this.closeStream()
    clearTimeout(this.hiddenTimer)
    clearInterval(this.pingTimer)
    clearTimeout(this.pongTimer)
    this.lockAbort?.abort()
    this.releaseLock?.()
    this.releaseLock = null
    this.ch?.close()
    this.ch = null
    this.mark()
  }

  /* ── leadership ─────────────────────────────────────────────────────────── */

  private requestLock(steal: boolean) {
    if (this.stopped) return
    this.lockAbort?.abort()
    const ac = new AbortController()
    this.lockAbort = steal ? null : ac
    const opts: LockOptions = steal ? { steal: true } : { signal: ac.signal }
    navigator.locks
      .request(this.o.key, opts, () => {
        if (this.stopped) return
        return new Promise<void>((resolve) => {
          this.releaseLock = resolve
          this.becomeLeader()
        })
      })
      // aborted (we stopped) or stolen by a tab that found us frozen: follow again
      .catch(() => {
        if (this.stopped || !this.leader) return
        this.demote()
      })
  }

  /** data-sse-<stream>="leader" | "follower" on <html>: which tab holds the stream (tests, devtools). */
  private mark() {
    const a = `data-sse-${this.o.key.replace(/^strevix-live-/, '')}`
    if (this.stopped) document.documentElement.removeAttribute(a)
    else document.documentElement.setAttribute(a, this.leader ? 'leader' : 'follower')
  }

  private becomeLeader() {
    if (this.stopped) return
    this.leader = true
    this.mark()
    this.ready = true
    clearInterval(this.pingTimer)
    clearTimeout(this.pongTimer)
    this.post({ t: 'leader', from: this.id })
    this.paused = false
    this.openStream()
    this.checkHidden()
  }

  /** The lock was taken from us: close the stream, become a follower and queue for the lock again. */
  private demote() {
    this.leader = false
    this.mark()
    this.releaseLock = null
    this.closeStream()
    clearTimeout(this.hiddenTimer)
    this.visibleTabs.clear()
    this.ready = false
    this.post({ t: 'hello', from: this.id, visible: isVisible() })
    this.armPing()
    this.requestLock(false)
  }

  /* ── messages ───────────────────────────────────────────────────────────── */

  private post(m: Msg) {
    try {
      this.ch?.postMessage(m)
    } catch {
      // the channel is closed: nothing to tell
    }
  }

  private onMessage(m: Msg) {
    if (this.stopped) return
    if (this.leader) {
      switch (m.t) {
        case 'hello':
          this.noteVisible(m.from, m.visible)
          this.post({ t: 'welcome', to: m.from, state: this.state, info: this.info, snapshot: this.o.snapshot?.() ?? null })
          return
        case 'vis':
          this.noteVisible(m.from, m.visible)
          return
        case 'ping':
          this.noteVisible(m.from, m.visible)
          this.post({ t: 'pong', to: m.from })
          return
        case 'bye':
          this.visibleTabs.delete(m.from)
          this.checkHidden()
          return
        default:
          return
      }
    }
    switch (m.t) {
      case 'welcome':
        if (m.to !== this.id) return
        clearTimeout(this.pongTimer)
        if (m.snapshot) this.o.onEvent(m.snapshot)
        this.ready = true
        this.setState(m.state, m.info)
        return
      case 'event':
        // a new leader's first event is the center's snapshot: it makes a joining tab whole
        if (!this.ready && m.e.name === (this.o.snapshotEvent ?? 'snapshot')) this.ready = true
        if (this.ready) this.o.onEvent(m.e)
        return
      case 'state':
        this.setState(m.state, m.info)
        return
      case 'leader':
        // a new leader: tell it whether we are visible, and ask for the state if we have none
        clearTimeout(this.pongTimer)
        this.post({ t: 'hello', from: this.id, visible: isVisible() })
        return
      case 'pong':
        if (m.to === this.id) clearTimeout(this.pongTimer)
        return
      default:
        return
    }
  }

  /** Follower: ask the leader now and then while visible; no answer → take over. */
  private armPing() {
    clearInterval(this.pingTimer)
    if (!this.share || this.leader) return
    this.pingTimer = setInterval(() => this.ping(), PING_MS)
  }

  private ping() {
    if (this.stopped || this.leader || !isVisible()) return
    this.post({ t: 'ping', from: this.id, visible: true })
    clearTimeout(this.pongTimer)
    this.pongTimer = setTimeout(() => void this.takeOverIfFrozen(), PONG_WAIT_MS)
  }

  private async takeOverIfFrozen() {
    if (this.stopped || this.leader) return
    try {
      const q = await navigator.locks.query()
      const held = q.held?.some((l) => l.name === this.o.key)
      // nobody holds it: our queued request gets it by itself
      if (!held || this.stopped || this.leader) return
    } catch {
      return
    }
    this.requestLock(true)
  }

  /* ── visibility ─────────────────────────────────────────────────────────── */

  private onVisibility = () => {
    if (this.stopped) return
    const v = isVisible()
    if (this.leader) {
      this.checkHidden()
      return
    }
    this.post({ t: 'vis', from: this.id, visible: v })
    if (v) this.ping()
  }

  private onPageHide = (e: PageTransitionEvent) => {
    // a page that goes into the back/forward cache comes back: leave only for good
    if (!e.persisted) this.stop()
  }

  private noteVisible(from: string, visible: boolean) {
    if (visible) this.visibleTabs.set(from, Date.now())
    else this.visibleTabs.delete(from)
    this.checkHidden()
  }

  private anyVisible(): boolean {
    if (isVisible()) return true
    const now = Date.now()
    for (const [id, at] of this.visibleTabs) {
      if (now - at <= VISIBLE_STALE_MS) return true
      this.visibleTabs.delete(id)
    }
    return false
  }

  /** Leader: close the stream after hiddenCloseMs() with every tab hidden; open it when one is visible. */
  private checkHidden() {
    if (!this.leader || this.stopped) return
    clearTimeout(this.hiddenTimer)
    if (this.anyVisible()) {
      if (this.paused) {
        this.paused = false
        this.delay = 2000
        this.openStream()
      }
      // followers that stop pinging (closed without a goodbye) fall out of the count
      if (!isVisible()) this.hiddenTimer = setTimeout(() => this.checkHidden(), VISIBLE_STALE_MS)
      return
    }
    if (this.paused) return
    this.hiddenTimer = setTimeout(() => {
      if (!this.leader || this.stopped || this.anyVisible()) {
        this.checkHidden()
        return
      }
      this.paused = true
      this.closeStream()
      this.setState('paused', {})
    }, hiddenCloseMs())
  }

  /* ── the stream (leader) ────────────────────────────────────────────────── */

  private setState(state: SharedSseState, info: SharedSseInfo) {
    // the browser's retries repeat the error: tell a state once
    if (state === this.state && JSON.stringify(info) === JSON.stringify(this.info) && state !== 'connecting') return
    this.state = state
    this.info = info
    this.o.onState(state, info)
    if (this.leader) this.post({ t: 'state', state, info })
  }

  private emit(e: SharedSseEvent) {
    this.o.onEvent(e)
    this.post({ t: 'event', e })
  }

  private closeStream() {
    clearTimeout(this.retryTimer)
    this.es?.close()
    this.es = null
  }

  private openStream() {
    if (!this.leader || this.stopped || this.paused || typeof EventSource === 'undefined') return
    this.closeStream()
    if (this.state !== 'limited' && this.state !== 'reconnecting') this.setState('connecting', {})
    const es = new EventSource(this.o.path, { withCredentials: true })
    this.es = es
    es.onopen = () => {
      if (this.es !== es) return
      this.delay = 2000
      this.setState('open', {})
    }
    es.onerror = () => {
      if (this.es !== es) return
      if (es.readyState !== 2) {
        // the browser reconnects by itself
        if (this.state !== 'limited') this.setState('reconnecting', {})
        return
      }
      // closed: the server refused the stream (429 by the limit of decision 51, or another answer)
      es.close()
      this.es = null
      void probeSse(this.o.path).then((r) => this.refused(r))
    }
    for (const name of this.o.events) {
      es.addEventListener(name, (ev) => {
        if (this.es !== es) return
        const m = ev as MessageEvent<string>
        this.emit({ name, data: m.data, lastEventId: m.lastEventId || undefined })
      })
    }
  }

  private refused(r: SseRefusal) {
    if (!this.leader || this.stopped || this.paused || this.es) return
    const wait = r.status === 429 ? (r.retryAfterMs ?? this.delay) : this.delay
    if (r.status === 429) this.setState('limited', { retryAt: Date.now() + wait, limit: r.limit, open: r.open })
    else this.setState('reconnecting', { refused: true })
    if (r.status !== 429) this.delay = Math.min(this.delay * 2, 30_000)
    clearTimeout(this.retryTimer)
    this.retryTimer = setTimeout(() => this.openStream(), wait)
  }
}
